import React, { useEffect, useContext, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notification, Modal, Row, Col } from 'antd';
import { AppContext } from 'App.context';
import ContentBox from 'Components/common/ContentBox/ContentBox';
import DefaultLayout from 'Components/DefaultLayout/DefaultLayout';
import paymentMethodConfiguredRequest from 'utils/networking/api/auth/paymentMethodConfigured.service';
import AccountOwnerDetail from './AccountOwnerDetail/AccountOwnerDetail';
import ChangePasswordForm from './ChangePasswordForm/ChangePasswordForm';
import PaymentMethods from './PaymentMethods/PaymentMethods';
import AccountFunctionalities from './AccountFunctionalities/AccountFunctionalities';
import NewHotelSelector from 'Components/common/HotelSelector_new/NewHotelSelector';
import AppButton from 'Components/common/Forms/AppButton/AppButton';
import DisableLicenceForm from './DisableLicenceForm/DisableLicenceForm';
import DisableLicenceManager from './DisableLicenceManager/DisableLicenceManager';
import { useShowNoPermissionNotification } from 'Components/common/Notifications/showNoPermissionNotification';

export default function AccountDetail() {
  const {
    appState,
    appActions: { translate, getPaymentMethods },
  } = useContext(AppContext);

  const showNoPermission = useShowNoPermissionNotification();

  const navigate = useNavigate();
  const location = useLocation();

  const isOwnerUser = appState.userId === appState.ownerId;

  const params = new URLSearchParams(window.location.search);

  const [selectedHotels, setSelectedHotels] = useState([]);
  const isSuperUser = appState.superUser === true;
  const isAdmin = appState.role === 'admin' || appState.userType === 'admin';

  useEffect(() => {
    if (params.get('registerPaymentMethod')) {
      const registerPaymentMethod = params.get('registerPaymentMethod');
      if (registerPaymentMethod === 'success') {
        paymentMethodConfigured(params.get('email'));
      } else {
        notification.warn({
          message: translate('register.registerKO'),
        });
      }
      params.delete('registerPaymentMethod');
      params.delete('email');

      navigate(location.pathname, {
        state: {
          registerPaymentMethod: params.toString(),
          email: params.toString(),
        },
      });
    } else {
      getPaymentMethods(appState.ownerId);
    }
  }, []);

  function paymentMethodConfigured(email) {
    paymentMethodConfiguredRequest({
      email,
      lang: appState.currentLanguage?.id,
    })
      .then(_data => {
        notification.success({
          message: translate('paymentMethods.addedSuccess'),
        });
        setTimeout(() => {
          getPaymentMethods(appState.ownerId);
        }, 1000);
      })
      .catch(error => {
        notification.error({
          message: error?.response?.data?.message,
        });
      });
  }

  const [state, setState] = useState({
    showUnsuscribeWizard: false,
  });

  function toggleUnsuscribeWizard() {
    setState(prev => ({
      ...prev,
      showUnsuscribeWizard: !prev.showUnsuscribeWizard,
    }));
  }

  // Add a counter to force remount of DisableLicenceManager
  const [disableLicenceManagerKey, setDisableLicenceManagerKey] = useState(0);
  function toggleDisableLicenceManager() {
    setState(prev => ({
      ...prev,
      showDisableLicenceManager: !prev.showDisableLicenceManager,
    }));
    setDisableLicenceManagerKey(prev => prev + 1);
  }

  const disableLicenceFormRef = useRef();

  return (
    <>
      <DefaultLayout>
        <ContentBox title={translate('user.accountDetailTitle')}>
          <AccountOwnerDetail userId={appState.ownerId} />
        </ContentBox>
        {isOwnerUser && (
          <ContentBox title={translate('user.changePasswordTitle')}>
            <ChangePasswordForm />
          </ContentBox>
        )}
        <ContentBox title={translate('layout.paymentMethods')}>
          <PaymentMethods paymentMethod={appState.paymentMethods} userId={appState.ownerId} />
        </ContentBox>
        <ContentBox title={translate('accountDetail.billing')} noPadding>
          <span>
            <strong>{translate('hotelSelector.info')}</strong>
          </span>
          <NewHotelSelector
            enableAllHotels={true}
            mode={appState.superUser ? 'multiple' : 'single'} // multiple for superUsers
            onHotelSelected={setSelectedHotels}
            autoSelect={true}
          />
          <div style={{ height: 16 }} />
          <AccountFunctionalities selectedHotel={selectedHotels} />

          <Row justify="center" centered style={{ marginTop: 24 }}>
            {(isOwnerUser || isSuperUser || isAdmin) && (
              <Col span={12} style={{ display: 'flex', justifyContent: 'center' }}>
                <AppButton
                  secondary={true}
                  text={translate('accountDetail.unsuscribe')}
                  action={() => {
                    if (isSuperUser || isOwnerUser) toggleUnsuscribeWizard();
                    else showNoPermission('account_detail', 'try_licence_end_request', {});
                  }}
                  style={{ width: '60%' }}
                />
              </Col>
            )}
            {(isOwnerUser || isSuperUser) && (
              <Col span={12} style={{ display: 'flex', justifyContent: 'center' }}>
                <AppButton
                  width
                  secondary={true}
                  text={translate('accountDetail.disableLicenceManager')}
                  action={() => toggleDisableLicenceManager()}
                  style={{ width: '60%' }}
                />
              </Col>
            )}
          </Row>
        </ContentBox>
      </DefaultLayout>

      <Modal
        title={null}
        open={state.showUnsuscribeWizard}
        centered
        onCancel={() => {
          if (disableLicenceFormRef.current) {
            disableLicenceFormRef.current.resetForm();
          }
          toggleUnsuscribeWizard();
        }}
        width={'30vw'}
        bodyStyle={{ height: '60vh' }}
        footer={null}
      >
        <DisableLicenceForm ref={disableLicenceFormRef} />
      </Modal>

      <Modal
        title={null}
        open={state.showDisableLicenceManager}
        centered
        onCancel={toggleDisableLicenceManager}
        footer={null}
        width={'80vw'}
      >
        <DisableLicenceManager
          key={disableLicenceManagerKey}
          open={state.showDisableLicenceManager}
        />
      </Modal>
    </>
  );
}
